import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, TextField, Button, Alert, CircularProgress, Divider } from '@mui/material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../src/firebase.marketplace';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../firebase.marketplace';
import { PaymentOutlined, SaveOutlined, ArrowBackOutlined } from '@mui/icons-material';

const PaymentCredentialsManager = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userRoles, setUserRoles] = useState({});
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) {
        navigate('/marketplace/auth/signin');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Get user document from Firestore
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Set user roles
          setUserRoles(userData.roles || {});
          
          // Check if user has artist role
          if (!userData.roles || !userData.roles.artist) {
            setError('You must be an artist to manage payment credentials');
            setLoading(false);
            return;
          }
          
          // Get existing payment API credentials if available
          if (userData.paymentApiCredentials) {
            setCredentials({
              username: userData.paymentApiCredentials.username || '',
              password: userData.paymentApiCredentials.password || ''
            });
          }
        } else {
          setError('User profile not found');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user profile data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser, navigate]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!credentials.username.trim() || !credentials.password.trim()) {
      setError('Both username and password are required');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Update user document with payment API credentials
      await updateDoc(doc(db, 'users', currentUser.uid), {
        paymentApiCredentials: {
          username: credentials.username,
          password: credentials.password
        },
        updatedAt: new Date()
      });
      
      setSuccess('Payment API credentials saved successfully');
    } catch (err) {
      console.error('Error saving payment credentials:', err);
      setError('Failed to save payment credentials');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        bgcolor: 'background.paper',
        borderRadius: 2,
        p: 3
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      p: 3,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 3
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate('/marketplace/profile')}
          sx={{ mr: 2 }}
        >
          Back to Profile
        </Button>
        <Typography variant="h5" component="h1">
          Payment API Credentials
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          <PaymentOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
          Manage Your Payment API Credentials
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          These credentials are used to process payments for your products in the Konzo marketplace.
          You can obtain your payment API credentials from your payment service provider.
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          If you don't provide your own credentials, the platform's default payment credentials will be used.
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="API Username"
            name="username"
            value={credentials.username}
            onChange={handleInputChange}
            margin="normal"
            variant="outlined"
            required
            InputLabelProps={{ shrink: true }}
          />
          
          <TextField
            fullWidth
            label="API Password"
            name="password"
            value={credentials.password}
            onChange={handleInputChange}
            margin="normal"
            variant="outlined"
            required
            type="password"
            InputLabelProps={{ shrink: true }}
          />
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveOutlined />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Credentials'}
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Alert severity="info">
        <Typography variant="body2">
          <strong>Security Note:</strong> Your payment API credentials are stored securely and are only used for processing payments for your products.
        </Typography>
      </Alert>
    </Box>
  );
};

export default PaymentCredentialsManager;
